/**
 * Shared utilities for action status logic and icon rendering
 */

import { AlertTriangle, CheckCircle, XCircle } from '@untitledui/icons';
import React, { type ReactNode } from 'react';

import type { ProposedAction } from '../core/types';

/**
 * Surface-level status the UI cares about.
 *
 * `indeterminate` is distinct from `error`/`success` because the system
 * cannot tell whether the action's side effect committed — the user must
 * verify externally and resolve via the resolve-indeterminate endpoint.
 * Showing it with the same red XCircle as a real error is a UX bug
 * (UX expert finding #2 on PR #5709).
 */
export type ActionStatus = 'pending' | 'success' | 'error' | 'indeterminate';

/**
 * Get execution status of an action.
 *
 * Only shows status for actions that were selected for execution.
 *
 * Ordering note: the `indeterminate` check runs BEFORE the `!executed_at`
 * guard so the indeterminate branch can never be masked as `pending` if a
 * future code path forgets to stamp `executed_at` alongside the status
 * (cubic finding P2 on PR #5709). Today every backend path that writes
 * `status: 'indeterminate'` also sets `executed_at` (executor.py:462,
 * migration 0035:122), but we don't want this UI to depend on that
 * invariant holding. Deselected actions are still suppressed first — a
 * deselected indeterminate action shouldn't surface a verification pill
 * the user can't act on.
 */
export function getActionStatus(action: ProposedAction): ActionStatus {
  // Deselected actions are not user-actionable, so suppress status
  // regardless of any execution_result payload they may carry.
  if (action.is_selected === false) {
    return 'pending';
  }

  const status = action.execution_result?.status;

  // Indeterminate is checked before the executed_at guard so a missing
  // executed_at (defensive) cannot collapse it to pending.
  if (status === 'indeterminate') {
    return 'indeterminate';
  }

  // Skipped actions were pruned from execution — neutral, not an error.
  if (status === 'skipped') {
    return 'pending';
  }

  // For every other status, the action must actually have been run.
  if (!action.executed_at) {
    return 'pending';
  }

  // Treat 'error', 'failure', and 'partial_success' as error states
  if (
    status === 'error' ||
    status === 'failure' ||
    status === 'partial_success'
  ) {
    return 'error';
  }

  // Only 'success' status is treated as success, unknown statuses default to error
  if (status === 'success') {
    return 'success';
  }

  return 'error';
}

/** Result statuses that record a failed execution attempt. */
const FAILED_RESULT_STATUSES: ReadonlySet<string> = new Set([
  'error',
  'failure',
  'partial_success',
]);

/**
 * Whether the action's raw `execution_result` records an actual failed attempt
 * (`error`, `failure`, or `partial_success`).
 *
 * This is narrower than `getActionStatus(action) === 'error'`: that classifier
 * has a fallback that also maps unknown and transient (`in_flight`) statuses to
 * `'error'`. Gate failure-only UI on THIS predicate so an expected, non-failure
 * outcome — e.g. a selected action skipped because its dependency was
 * deselected — is never labelled as a failure.
 */
export function hasFailedExecutionResult(action: ProposedAction): boolean {
  const status = action.execution_result?.status;
  return status !== undefined && FAILED_RESULT_STATUSES.has(status);
}

/**
 * Whether a stored non-retryable failure verdict still describes the proposal
 * as currently composed.
 *
 * A deterministic (non-retryable) failure is a verdict on the exact selected
 * action set that was attempted — not on any action in isolation (e.g. a
 * cardinality conflict exists only because two actions were in the same
 * batch). Any composition change voids the verdict, so a retry is meaningful
 * again. Composition has changed when either:
 *
 * - a selected action carries no execution attempt (`executed_at` null) — an
 *   action was added since the attempt, or edited (any payload edit resets
 *   execution state on the backend, whether it snapshots a new version or
 *   overwrites the current one in place); or
 * - an action with a failed attempt is now deselected — the failed batch
 *   shrank.
 *
 * `selectedActionIds` is the LIVE selection when the caller tracks it in
 * client state ahead of the server round-trip (StickyFooter's prop); when
 * provided it overrides each action's persisted `is_selected`, so unchecking
 * a failed action lifts the verdict immediately. `undefined` means "no live
 * tracking — trust the persisted flags".
 *
 * Precondition: only meaningful when the proposal actually has a failed
 * attempt recorded; callers AND it with their failure state. On a
 * never-executed proposal it trivially returns true (every selected action is
 * unexecuted), which is why it must not be read on its own.
 */
export function isFailureVerdictStale(
  actions: readonly ProposedAction[],
  selectedActionIds?: readonly number[]
): boolean {
  return actions.some(action => {
    const isSelected =
      selectedActionIds !== undefined
        ? selectedActionIds.includes(action.id)
        : action.is_selected !== false;
    if (isSelected) {
      return !action.executed_at;
    }
    const status = action.execution_result?.status;
    return status !== undefined && FAILED_RESULT_STATUSES.has(status);
  });
}

/**
 * Return the best raw technical detail string for a failed action, or null.
 *
 * DEVELOPER-FACING — callers must render this only behind an opt-in
 * disclosure, never as primary user-visible text (tone guide §'Never Expose
 * System Internals').
 */
export function getActionErrorDetail(action: ProposedAction): string | null {
  if (getActionStatus(action) !== 'error') {
    return null;
  }
  const result = action.execution_result;
  if (!result) {
    return null;
  }
  if (result.error) {
    return result.error;
  }
  const firstValidationError = result.validation_errors?.[0];
  if (firstValidationError?.message) {
    return firstValidationError.message;
  }
  return null;
}

/**
 * Render status icon for an action
 * - pending      → null
 * - success      → green CheckCircle
 * - indeterminate → amber AlertTriangle with a tooltip-bearing `title`
 * - error/other  → red XCircle
 */
export function renderStatusIcon(action: ProposedAction): ReactNode {
  const status = getActionStatus(action);

  if (status === 'pending') {
    return null;
  }

  if (status === 'success') {
    return (
      <CheckCircle
        className='size-4 text-fg-success-secondary'
        data-component='status-icon'
        data-instance={action.id}
        data-state='success'
        data-testid={`status-icon-success-${action.id}`}
        aria-hidden='true'
      />
    );
  }

  if (status === 'indeterminate') {
    return (
      <AlertTriangle
        className='size-4 text-fg-warning-secondary'
        data-component='status-icon'
        data-instance={action.id}
        data-state='indeterminate'
        data-testid={`status-icon-indeterminate-${action.id}`}
        aria-label='Outcome unverified – manual verification required'
      >
        <title>Outcome unverified – manual verification required</title>
      </AlertTriangle>
    );
  }

  return (
    <XCircle
      className='size-4 text-fg-error-secondary'
      data-component='status-icon'
      data-instance={action.id}
      data-state='error'
      data-testid={`status-icon-error-${action.id}`}
      aria-hidden='true'
    />
  );
}
